import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Typography,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom"; // Importer useParams
import { useTranslation } from "react-i18next";
import '../styles.css';
import ArmonKehilati from "../components/templates/armon/ArmonKehilati";
import ArmonAlef from "../components/templates/armon/ArmonAlef";
import ArmonAlef2 from "../components/templates/armon/ArmonAlef2";
import ArmonBaavad from "../components/templates/armon/ArmonBaavad";
import LayoutRenderer from "../components/layout/LayoutRenderer";
import styled, { keyframes } from 'styled-components';
import { updateVariableTimes } from '../utils/scheduleUtils';
import { getFontSizes } from '../utils/fontSizeUtils';
import { parseZmanimSettings } from '../utils/zmanimSettingsUtils';

const fillAnimation = keyframes`
  from {
    clip-path: polygon(0% 0%, 0% 100%, 0% 100%, 0% 0%);
  }
  to {
    clip-path: polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%);
  }
`;

const LoadingContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url('/images/Armon/fond.png');
  background-size: cover;
  background-position: center;
  position: relative;
`;

const BaseLogoLayer = styled.img`
  width: 150px;
  height: auto;
  opacity: 0.3;
  position: absolute;
`;

const FillingLogoLayer = styled.img`
  width: 150px;
  height: auto;
  position: absolute;
  animation: ${fillAnimation} 2s ease-in-out infinite;
`;

const CommunityDisplay = ({ enableLayoutRenderer = false }) => {
  const didLogFirstRender = useRef(false);
  if (!didLogFirstRender.current) {
    didLogFirstRender.current = true;
    // eslint-disable-next-line no-console
    console.info("[render] CommunityDisplay");
    if (typeof window !== "undefined") {
      window.__KEHILATI_MOUNT_LOGS__ = window.__KEHILATI_MOUNT_LOGS__ || [];
      window.__KEHILATI_MOUNT_LOGS__.push({ type: "render", name: "CommunityDisplay", at: Date.now() });
    }
  }

  const { communityName } = useParams(); // Récupérer le communityId depuis l'URL
  const [searchParams] = useSearchParams();
  const screenNumber = parseInt(searchParams.get("screenNumber")) || 1; // Par défaut 1 si non spécifié
  const isMainScreen = searchParams.get("ismainscreen")?.toLowerCase() === "true" || 
                      searchParams.get("isMainScreen")?.toLowerCase() === "true"; // Convertir en booléen
  const [featureConfig, setFeatureConfig] = useState(null);
  const [community, setCommunity] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [sponsor, setSponsor] = useState(null);
  const [shiur, setShiur] = useState(null);
  const [zmanim, setZmanim] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [adImages, setAdImages] = useState([]);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Gestion des erreurs
  const { i18n } = useTranslation();
  const [eventSource, setEventSource] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 5;
  const RETRY_DELAY = 3000; // 3 secondes
  const [language, setLanguage] = useState(null);
  const [dailyLearning, setDailyLearning] = useState(null);
  const [nextEvents, setNextEvents] = useState(null);
  const [parasha, setParasha] = useState(null);
  const [omer, setOmer] = useState(null);
  const [holidayInformation, setHolidayInformation] = useState(null);
  const [layoutConfig, setLayoutConfig] = useState(null);

  const fetchJson = async (url, { label } = {}) => {
    const res = await fetch(url);
    const contentType = res.headers.get("content-type") || "";
    const text = await res.text();

    if (!res.ok) {
      throw new Error(
        `${label || "request"} failed (${res.status}) ${url} content-type=${contentType} body=${text.slice(0, 120)}`
      );
    }

    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(
        `${label || "request"} expected JSON but got ${contentType || "unknown"} from ${url}: ${text.slice(0, 120)}`
      );
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info("[mount] CommunityDisplay");
    if (typeof window !== "undefined") {
      window.__KEHILATI_MOUNT_LOGS__ = window.__KEHILATI_MOUNT_LOGS__ || [];
      window.__KEHILATI_MOUNT_LOGS__.push({ type: "mount", name: "CommunityDisplay", at: Date.now() });
    }
  }, []);

  // Créer un ID unique pour ce client
  const clientId = useMemo(() => Math.random().toString(36).substring(7), []);

  useEffect(() => {
    const loadCfg = async () => {
      try {
        const cfg = await fetchJson(`${process.env.REACT_APP_BASE_URL}/config/features`, { label: "features" });
        setFeatureConfig(cfg);
      } catch {
        // default behavior is safe (mainScreenLogic disabled)
        setFeatureConfig({
          features: { tv: { mainScreenLogic: { enabled: false }, periodicRefresh: { intervalMs: 2 * 60 * 60 * 1000 } } },
        });
      }
    };
    loadCfg();
  }, []);

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language.toLowerCase()); // Change la langue (en minuscule pour i18n)
    }
  }, [language, i18n]);
  


  const getLocationData = async (communityData) => {
    // Vérifier que les coordonnées de la communauté sont disponibles
    if (communityData?.latitude && communityData?.longitude && communityData?.timezone) {
      return {
        latitude: communityData.latitude,
        longitude: communityData.longitude,
        timezone: communityData.timezone
      };
    }

    // Si les coordonnées ne sont pas disponibles, on lance une erreur
    throw new Error("Coordonnées de la communauté non disponibles");
  };

  const fetchZmanim = async (communityData) => {
    try {
      const location = await getLocationData(communityData);
      
      // Utiliser la date locale du navigateur
      const now = new Date();
      const currentDate = now.toLocaleDateString('en-CA'); // Format yyyy-mm-dd en utilisant la date locale
      
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getZmanim?latitude=${location.latitude}&longitude=${location.longitude}&timezone=${location.timezone}&date=${currentDate}&communityName=${communityName}`
      );
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des zmanim.");
      }
      const zmanimData = await response.json();

      // Transformer les zmanim selon les settings
      const transformedZmanim = {};
      const zmanimSettings = parseZmanimSettings(communityData.zmanimSettings);
      // DB defaults used "plagHamincha" while API keys use "plagHaMincha"
      const zmanimNameAliases = { plagHamincha: "plagHaMincha" };

      zmanimSettings.forEach((setting) => {
        if (!setting.displayed) return;
        const apiKey =
          zmanimData.zmanim[setting.name] != null
            ? setting.name
            : zmanimNameAliases[setting.name] != null &&
                zmanimData.zmanim[zmanimNameAliases[setting.name]] != null
              ? zmanimNameAliases[setting.name]
              : null;
        if (apiKey != null) {
          const label = setting.customName || setting.name;
          transformedZmanim[label] = zmanimData.zmanim[apiKey];
        }
      });

      if (
        Object.keys(transformedZmanim).length === 0 &&
        zmanimData.zmanim &&
        typeof zmanimData.zmanim === "object"
      ) {
        Object.entries(zmanimData.zmanim).forEach(([k, v]) => {
          if (v != null && v !== "") {
            transformedZmanim[k] = v;
          }
        });
      }

      return {
        ...zmanimData,
        zmanim: zmanimData.zmanim,  // Garde les zmanim originaux
        zmanimFormatted: transformedZmanim  // Ajoute la version formatée
      };
    } catch (err) {
      throw err;
    }
  };

  const fetchDailyLearning = async (communityData) => {
    try {
      const location = await getLocationData(communityData);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getTodayLearning?language=${communityData.displayLanguage}&latitude=${location.latitude}&longitude=${location.longitude}&timezone=${location.timezone}`
      );
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération du daily learning.");
      }
      return response.json();
    } catch (err) {
      throw err;
    }
  };

  const fetchCalendar = async (communityData) => {
    try {
      const location = await getLocationData(communityData);
      
      // Utiliser la date locale du navigateur
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 7);
      
      const startDateStr = today.toLocaleDateString('en-CA'); // Format yyyy-mm-dd
      const endDateStr = endDate.toLocaleDateString('en-CA'); // Format yyyy-mm-dd

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getCalendar?language=${communityData.displayLanguage}&latitude=${location.latitude}&longitude=${location.longitude}&timezone=${location.timezone}&startDate=${startDateStr}&endDate=${endDateStr}&communityName=${communityName}`
      );
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération du calendrier.");
      }
      return response.json();
    } catch (err) {
      throw err;
    }
  };

  const fetchParasha = async (communityData) => {
    try {
      const location = await getLocationData(communityData);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getParasha?language=${communityData.displayLanguage}&latitude=${location.latitude}&longitude=${location.longitude}&timezone=${location.timezone}&communityName=${communityName}`
      );
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération de la parasha.");
      }
      return response.json();
    } catch (err) {
      throw err;
    }
  };

  const fetchOmer = async (communityData) => {
    try {
      const location = await getLocationData(communityData);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getOmer?latitude=${location.latitude}&longitude=${location.longitude}&timezone=${location.timezone}`
      );
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération de l'Omer.");
      }
      return response.json();
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch community data with screenNumber
        const communityUrl = `${process.env.REACT_APP_BASE_URL}/community/getCommunityByUrlName/${communityName}?screenNumber=${screenNumber}`;
        const communityData = await fetchJson(communityUrl, { label: "community" });
        setCommunity(communityData);
        setLanguage(communityData.displayLanguage);
        setLogo(`${communityData.logoPath}`);

        // Fetch other data
        const [
          scheduleData,
          adData,
          templateData,
          layoutData,
          sponsorData,
          shiurData,
          zmanimData,
          dailyLearningData,
          calendarData,
          parashaData,
          omerData,
        ] = await Promise.all([
          fetchJson(
            `${process.env.REACT_APP_BASE_URL}/tefilot/getCurrentTefilotCommunities?communityId=${communityData.id}`,
            { label: "schedule" }
          ),
          fetchJson(
            `${process.env.REACT_APP_BASE_URL}/ad/getAds/${communityData.id}`,
            { label: "ads" }
          ),
          fetchJson(
            `${process.env.REACT_APP_BASE_URL}/template/getSelectedTemplate?communityId=${communityData.id}&screenNumber=${screenNumber}`,
            { label: "template" }
          ),
          fetchJson(
            `${process.env.REACT_APP_BASE_URL}/layout/getLayout?communityId=${communityData.id}&screenNumber=${screenNumber}`,
            { label: "layout" }
          ),
          fetchJson(
            `${process.env.REACT_APP_BASE_URL}/sponsor/getCurrentSponsor/${communityData.id}`,
            { label: "sponsor" }
          ),
          fetchJson(
            `${process.env.REACT_APP_BASE_URL}/shiur/getShiurim?communityId=${communityData.id}`,
            { label: "shiur" }
          ),
          fetchZmanim(communityData),
          fetchDailyLearning(communityData),
          fetchCalendar(communityData),
          fetchParasha(communityData),
          fetchOmer(communityData),
        ]);

        // Mettre à jour les horaires variables et formater tous les horaires
        const updatedSchedule = updateVariableTimes(scheduleData, zmanimData, parashaData, communityData.displayLanguage, communityData);
        // console.log('Horaires mis à jour:', updatedSchedule);
        setSchedule(updatedSchedule);

        const selectedAds = adData
          .filter(ad => ad.isSelected)
          .map(ad => `${ad.filePath}`);
        setAdImages(selectedAds);
        
        setSelectedTemplate(templateData);
        setLayoutConfig(layoutData);
        setSponsor(sponsorData);
        setShiur(shiurData);
        setZmanim(zmanimData);
        setDailyLearning(dailyLearningData);
        setNextEvents(calendarData);
        setParasha(parashaData);
        setOmer(omerData);
        // Ne charger les informations de fête que si selectedChag n'est pas vide
        if (communityData.selectedChag) {
          const holidayInfoResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL}/tefilot/getHolidayInformation?communityId=${communityData.id}&category=${communityData.selectedChag}`
          );
          if (holidayInfoResponse.ok) {
            const holidayInfoData = await holidayInfoResponse.json();
            console.log('Holiday Information Data:', holidayInfoData);
            setHolidayInformation(holidayInfoData);
          }
        } else {
          setHolidayInformation(null);
        }

      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        setError(err?.message || "Une erreur est survenue lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [communityName]);

  // Fonction pour connecter SSE
  const connectSSE = () => {
    if (!community?.id) {
      console.log("[SSE Client] Pas de connexion SSE: communityId manquant");
      return;
    }

    disconnectSSE();

    console.log(`[SSE Client] Création connexion - ClientId: ${clientId}, Tentative: ${retryCount + 1}`);
    const mainScreenLogicEnabled = featureConfig?.features?.tv?.mainScreenLogic?.enabled === true;
    const shouldConnect = mainScreenLogicEnabled ? isMainScreen : true;
    if (!shouldConnect) {
      console.log("[SSE Client] Connexion SSE ignorée (main screen logic activée, écran non principal)");
      return;
    }
    const source = new EventSource(
      `${process.env.REACT_APP_BASE_URL}/events/${community.id}?isMainScreen=true&clientId=${clientId}`
    );

    source.onopen = () => {
      console.log(`[SSE Client] Connexion établie - ClientId: ${clientId}`);
      setRetryCount(0); // Réinitialiser le compteur après une connexion réussie
    };

    source.onerror = (error) => {
      console.error(`[SSE Client] Erreur connexion - ClientId: ${clientId}`, error);
      source.close();
      setEventSource(null);

      // Logique de reconnexion
      if (retryCount < MAX_RETRIES) {
        console.log(`[SSE Client] Tentative de reconnexion dans ${RETRY_DELAY/1000}s - Tentative ${retryCount + 1}/${MAX_RETRIES}`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          connectSSE();
        }, RETRY_DELAY);
      } else {
        console.log("[SSE Client] Nombre maximum de tentatives atteint");
        // Optionnel : réinitialiser après un délai plus long
        setTimeout(() => {
          setRetryCount(0);
          connectSSE();
        }, RETRY_DELAY * 4);
      }
    };

    source.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("Mise à jour SSE reçue:", data);

      // Gérer l'événement de rafraîchissement
      if (data.type === 'REFRESH_PAGE') {
        console.log("Commande de rafraîchissement reçue");
        window.location.reload();
        return;
      }

      // Mettre à jour uniquement les états qui ont reçu de nouvelles données
      if (data.schedule) {
        // Utiliser les nouveaux zmanim s'ils sont fournis, sinon utiliser les zmanim existants
        const currentZmanim = data.zmanim || zmanim;
        
        if (!currentZmanim) {
          console.error("Pas de zmanim disponibles pour mettre à jour les horaires variables");
          return;
        }

        // Mettre à jour les horaires variables avec les zmanim
        const updatedSchedule = updateVariableTimes(data.schedule, currentZmanim, parasha, language, community);
        console.log('Horaires mis à jour avec zmanim:', updatedSchedule);
        setSchedule(updatedSchedule);
      }
      if (data.holidayInformation) {
        setHolidayInformation(data.holidayInformation);
      }
      if (data.sponsor) {
        setSponsor(data.sponsor);
      }
      if (data.shiur) {
        setShiur(data.shiur);
      }
      if (data.zmanim) {
        setZmanim(data.zmanim);
        // Mettre à jour le schedule avec les nouveaux zmanim si on a un schedule
        if (schedule) {
          const updatedSchedule = updateVariableTimes(schedule, data.zmanim, parasha, language, community);
          setSchedule(updatedSchedule);
        }
      }
      if (data.community) {
        setCommunity(data.community);
      }
    };

    setEventSource(source);
  };

  // Fonction pour déconnecter SSE
  const disconnectSSE = () => {
    if (eventSource) {
      console.log(`[SSE Client] Déconnexion - ClientId: ${clientId}`);
      eventSource.close();
      setEventSource(null);
    }
  };

  useEffect(() => {
    const mainScreenLogicEnabled = featureConfig?.features?.tv?.mainScreenLogic?.enabled === true;
    const shouldConnect = mainScreenLogicEnabled ? isMainScreen : true;
    if (community?.id && shouldConnect) {
      console.log(`[SSE Client] Init connexion - ClientId: ${clientId}`);
      connectSSE();
    }

    return () => {
      disconnectSSE();
    };
  }, [community?.id, isMainScreen, featureConfig]);

  useEffect(() => {
    let refreshTimeouts = [];

    const scheduleRefresh = () => {
      const now = new Date();
      const refreshTimes = [
        { hour: 0, minute: 1 },  // Minuit 01
        { hour: 2, minute: 1 },  // Minuit 30
        { hour: 4, minute: 1 },  // Minuit 45
        { hour: 6, minute: 1 },  // 6h 01
        { hour: 8, minute: 1 },  // 8h 01
        { hour: 10, minute: 1 }, // 10h 01
        { hour: 12, minute: 1 }, // 12h 01
        { hour: 14, minute: 1 }, // 14h 01
        { hour: 16, minute: 1 }, // 16h 01
        { hour: 18, minute: 1 }, // 18h 01
        { hour: 20, minute: 1 }, // 20h 01
        { hour: 22, minute: 1 }, // 22h 01
      ];

      refreshTimes.forEach(({ hour, minute }) => {
        const nextRefresh = new Date(now);
        nextRefresh.setHours(hour, minute, 0, 0);

        // Si l'heure est déjà passée, programmer pour le lendemain
        if (
          (hour === 0 && now.getHours() === 0 && now.getMinutes() >= minute) ||
          (hour === 0 && now.getHours() > 0) ||
          (hour === 2 && now.getHours() === 2 && now.getMinutes() >= minute) ||
          (hour === 2 && now.getHours() > 2) ||
          (hour === 4 && now.getHours() === 4 && now.getMinutes() >= minute) ||
          (hour === 4 && now.getHours() > 4) ||
          (hour === 6 && now.getHours() === 6 && now.getMinutes() >= minute) ||
          (hour === 6 && now.getHours() > 6) ||
          (hour === 8 && now.getHours() === 8 && now.getMinutes() >= minute) ||
          (hour === 8 && now.getHours() > 8) ||
          (hour === 10 && now.getHours() === 10 && now.getMinutes() >= minute) ||
          (hour === 10 && now.getHours() > 10) ||
          (hour === 12 && now.getHours() === 12 && now.getMinutes() >= minute) ||
          (hour === 12 && now.getHours() > 12) ||
          (hour === 14 && now.getHours() === 14 && now.getMinutes() >= minute) ||
          (hour === 14 && now.getHours() > 14) ||
          (hour === 16 && now.getHours() === 16 && now.getMinutes() >= minute) ||
          (hour === 16 && now.getHours() > 16) ||
          (hour === 18 && now.getHours() === 18 && now.getMinutes() >= minute) ||
          (hour === 18 && now.getHours() > 18) ||
          (hour === 20 && now.getHours() === 20 && now.getMinutes() >= minute) ||
          (hour === 20 && now.getHours() > 20) ||
          (hour === 22 && now.getHours() === 22 && now.getMinutes() >= minute) ||
          (hour === 22 && now.getHours() > 22)
        ) {
          nextRefresh.setDate(nextRefresh.getDate() + 1);
        }
        
        const delay = nextRefresh.getTime() - now.getTime();
        // console.log(`Prochain refresh programmé pour ${nextRefresh.toLocaleString()} (dans ${Math.floor(delay/1000/60)} minutes)`);
        
        const timeoutId = setTimeout(() => {
          if (navigator.onLine) {
            console.log(`Rafraîchissement programmé à ${hour}:${minute} exécuté`);
            window.location.reload();
          } else {
            console.log("Pas de connexion internet - Report du rafraîchissement");
            scheduleRefresh(); // Reprogrammer les refreshs
          }
        }, delay);

        refreshTimeouts.push(timeoutId);
      });
    };

    if (zmanim?.zmanim?.tzeit) {
      scheduleRefresh();
    }

    return () => {
      // console.log("Nettoyage des timeouts de refresh");
      refreshTimeouts.forEach(clearTimeout);
    };
  }, [community, zmanim]); // Ajout de zmanim dans les dépendances

  // Ajouter un effet pour surveiller la connexion internet
  useEffect(() => {
    const handleOnline = () => {
      console.log("[SSE Client] Connexion internet rétablie - Tentative de reconnexion");
      setRetryCount(0);
      connectSSE();
    };

    const handleOffline = () => {
      console.log("[SSE Client] Connexion internet perdue");
      disconnectSSE();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [community?.id]);

  if (loading) return (
    <LoadingContainer>
      <BaseLogoLayer src="/images/kehilati.png" alt="Chargement..." />
      <FillingLogoLayer src="/images/kehilati.png" alt="Chargement..." />
    </LoadingContainer>
  );
  if (error) return <Typography color="error">{error}</Typography>;

  if (enableLayoutRenderer && layoutConfig) {
    return (
      <LayoutRenderer
        layoutConfig={layoutConfig}
        data={{
          community,
          schedule,
          adImages,
          logo,
          sponsor,
          shiur,
          zmanim,
          dailyLearning,
          nextEvents,
          parasha,
          holidayInformation,
          language,
          omer,
          fontSizes: getFontSizes(community?.fontSizeSetting),
        }}
      />
    );
  }

  // Switch entre les templates
  console.log('Template:',selectedTemplate?.templateJsFile)
  switch (selectedTemplate?.templateJsFile) {
    case "ArmonKehilati":
      return (
        <ArmonKehilati
          community={community}
          schedule={schedule}
          adImages={adImages}
          logo={logo}
          sponsor={sponsor}
          shiur={shiur}
          zmanim={zmanim}
          dailyLearning={dailyLearning}
          nextEvents={nextEvents}
          language={language}
          holidayInformation={holidayInformation}
          parasha={parasha}
          omer={omer}
          fontSizes={getFontSizes(community?.fontSizeSetting)}
        />
      );
    case "ArmonAlef":
      return (
        <ArmonAlef
          community={community}
          schedule={schedule}
          adImages={adImages}
          logo={logo}
          sponsor={sponsor}
          shiur={shiur}
          zmanim={zmanim}
          dailyLearning={dailyLearning}
          nextEvents={nextEvents}
          parasha={parasha}
          holidayInformation={holidayInformation}
          language={language}
          omer={omer}
          fontSizes={getFontSizes(community?.fontSizeSetting)}
        />
      );
    case "ArmonAlef2":
        return (
          <ArmonAlef2
            community={community}
            schedule={schedule}
            adImages={adImages}
            logo={logo}
            sponsor={sponsor}
            shiur={shiur}
            zmanim={zmanim}
            dailyLearning={dailyLearning}
            nextEvents={nextEvents}
            parasha={parasha}
            holidayInformation={holidayInformation}
            language={language}
            omer={omer}
            fontSizes={getFontSizes(community?.fontSizeSetting)}
          />
        );
    case "ArmonBaavad":
          return (
            <ArmonBaavad
              community={community}
              schedule={schedule}
              adImages={adImages}
              logo={logo}
              sponsor={sponsor}
              shiur={shiur}
              zmanim={zmanim}
              dailyLearning={dailyLearning}
              nextEvents={nextEvents}
              parasha={parasha}
              holidayInformation={holidayInformation}
              language={language}
              omer={omer}
              fontSizes={getFontSizes(community?.fontSizeSetting)}
            />
          );
    default:
      return <Typography>Aucun template disponible</Typography>;
  }
};

export default CommunityDisplay;